/**
 * @file plugins/kgAttend.js
 * @author Jake Stark
 * @desc Kindergarten Attendance report plugin

*/

/**
 * @module plugins/kgAttend
 * @desc Kindergarten Attendance report plugin
 */

define('plugins/kgAttend',[
        "models", "cardboard/models", "cardboard/collections", "cardboard/pluginPieces",
        "text!plugins/templates/kgAttend_overallAttendance_table.html!strip", "text!plugins/templates/kgAttend_attendanceByAgeScreened_table.html!strip",
        "text!plugins/templates/kgAttend_attendanceAndECEParticipation_table.html!strip"
    ],
    function (
        ECLDSModels, CardboardModels, CardboardCollections, PluginPieces,
        overallAttendanceTableTemplate, attendanceByAgeScreenedTableTemplate, attendanceAndECEParticipationTableTemplate) {

        console.log("MODULE: Kindergarten Attendance");

        var exports = {

            // other names by which this plugin is known. this exists for
            // WebFOCUS shim compatibility
            options: {
                orgDetailPane: false,
                noMapSearch: true,
                noOrgFinderSchool: true,
                orgFinderDistrict: true,
                forcedSummaryFilters: ["FISCAL_YEAR", "RACE_ETHTYPE"],
                definitionsECLDS: true
            },

            // the options available to these filters will be populated
            // from the database, via the below data sources
            filters: {
                // no filters!
            },

            // each of these is the configuration that gets passed to a
            // DataBoxPaneView.
            panes: [{
                title: "Kindergarten Overall Attendance",
                explanationText: '<p>This chart shows Minnesota public school kindergarten attendance rates. Attendance rates are determined for all public school students with average daily membership of at least one day. Average daily attendance and average daily membership are aggregated across student enrollment records within a school year. The aggregated average daily attendance divided by the aggregated average daily membership provides the percentages reported.</p><p> See "For More Information" section below to read more about attendance.</p>',
                openByDefault: true,

                views: [{
                        type: "template",
                        template: "<div class='covMsg'>{{data.covidMessage}}</div><h5>{{data.heading}}</h5> ",
                        dataSourceName: "data",
                        dataPath: "dataSets.overallAttendance"
                    },
                    {
                        type: "template",
                        template: "<p>NOTE: The total number of kindergartners in the Kindergarten Attendance report differs from other Kindergarten reports due to student-counting policies. Student enrollment is based on the count of students as of October 1 of the school year. Attendance is an end-of-year count of public school students with <a href='https://education.mn.gov/mdeprod/idcplg?IdcService=GET_FILE&dDocName=MDE074759&RevisionSelectionMethod=latestReleased&Rendition=primary' target='_blank'>average daily membership</a> (ADM) of at least one day. Generally, ADM is lower than enrollment due to such factors as student attrition, shared-time/homeschool students, and illness.</p><button style='float:right;' onclick='exportChart(this.parentNode.nextSibling.children[0].id);'>Export to PDF</button>"
                    },
                    {
                        // SETUP
                        type: "column",
                        dataSourceName: "data",
                        dataSetPath: "dataSets.overallAttendance",

                        // all paths in the
                        // chartable series are
                        // relative to this blob

                        // "pointsWithinRows": given that the data is
                        // two-dimensional (meaning an array of arrays, or
                        // similar structure, implying that we have more than
                        // one series to plot) each series is contained in a
                        // "row", like so:
                        //	 +---------+
                        //	 | X X X X |
                        //	 | . . . . |
                        //	 | . . . . |
                        //	 | . . . . |
                        //	 +---------+
                        dataShape: "pointsWithinRows",

                        // since in this case there's only one dimension, due to
                        // there being only one series to plot, it looks like
                        // this:
                        //	 +---------+
                        //	 | X X X X |
                        //	 +---------+
                        dataDimensions: 1,
                        colorByPoint: true,
                        exportToPDF: true,
                        hideContext: true,
                        title: "",

                        // DISPLAY
                        unitString: "%",
                        unitDec: 1,
                        showLegend: false,

                        // SERIES (only one series, this is a simple graph)
                        series: [{
                            seriesDisplayNamePath: "attendanceLevel",
                            dataPath: "",
                            valuePath: "percent",
                            displayNamePath: "attendanceLevel",
                        }],
                        errorConditions: [PluginPieces.standardErrorCondition]
                    },
                    {
                        type: "template",
                        dataSourceName: "data",
                        dataSetPath: "dataSets.overallAttendance",
                        template: overallAttendanceTableTemplate,
                        errorConditions: [PluginPieces.silentStandardErrorCondition]
                    }
                ]
            }, {
                title: "Kindergarten Attendance by Age Screened",
                explanationText: "This chart shows the relationship between the age children were screened and their public school kindergarten attendance. Screening may take place between the ages of 3 and the start of kindergarten or first grade. Screening unknown may include children who are receiving early childhood special education service, children who are screened by other providers but not entered into the system, or children who have no known screening record.",
                openByDefault: false,

                views: [{
                        type: "template",
                        template: "<div class='covMsg'>{{data.covidMessage}}</div><h5>{{data.heading}}</h5> ",
                        dataSourceName: "data",
                        dataPath: "dataSets.attendanceByAgeScreened"
                    }, {
                        type: "template",
                        template: "<p>NOTE: The total number of kindergartners in the Kindergarten Attendance report differs from other Kindergarten reports due to student-counting policies. Student enrollment is based on the count of students as of October 1 of the school year. Attendance is an end-of-year count of public school students with <a href='https://education.mn.gov/mdeprod/idcplg?IdcService=GET_FILE&dDocName=MDE074759&RevisionSelectionMethod=latestReleased&Rendition=primary' target='_blank'>average daily membership</a> (ADM) of at least one day. Generally, ADM is lower than enrollment due to such factors as student attrition, shared-time/homeschool students, and illness.</p><button style='float:right;' onclick='exportChart(this.parentNode.nextSibling.children[0].id);'>Export to PDF</button>"
                    }, {
                        // SETUP
                        type: "stackedColumn",
                        dataSourceName: "data",
                        dataSetPath: "dataSets.attendanceByAgeScreened",

                        // "pointsAcrossRows": given that the data is
                        // two-dimensional (meaning an array of arrays, or
                        // similar structure, implying that we have more than
                        // one series to plot) each series is contained in a
                        // "column", like so:
                        //	 +---------+
                        //	 | X . . . |
                        //	 | X . . . |
                        //	 | X . . . |
                        //	 | X . . . |
                        //	 +---------+
                        // To wrap your head around this, watch iteration in your debugger in
                        // pointsAcrossRows() in transforms.js.
                        dataShape: "pointsAcrossRows",
                        convertDataHashToArray: true,
                        dataDimensions: 2,
                        unitDec: 1,
                        title: "",

                        // DISPLAY
                        xAxisLabelPath: "slice",
                        legendReversed: false,
                        unitString: "%",
                        exportToPDF: true,
                        hideContext: true,
                        stackLabels: false,

                        showLegend: true,
                        disableLegendClick: false,

                        // SERIES (only one series, this is a simple graph)
                        series: [{
                                valuePath: "attendance[0].percent",
                                seriesDisplayName: "Below 90%",

                            }, {
                                valuePath: "attendance[1].percent",
                                seriesDisplayName: "90%-95%",
                            },
                            {
                                valuePath: "attendance[2].percent",
                                seriesDisplayName: "96%-100%",

                            }
                        ],
                        errorConditions: [PluginPieces.standardErrorCondition]
                    },
                    {
                        type: "template",
                        dataSourceName: "data",
                        dataSetPath: "dataSets.attendanceByAgeScreened",
                        template: attendanceByAgeScreenedTableTemplate,
                        errorConditions: [PluginPieces.silentStandardErrorCondition]
                    }
                ]
            }, {
                title: "Kindergarten Attendance and ECE Participation",
                explanationText: "This chart shows the relationship between children's early care and education (ECE) participation prior to entering public kindergarten and their subsequent public kindergarten attendance. Attendance rates are determined for all public school students with average daily membership of at least one day. Average daily attendance and average daily membership are aggregated across student enrollment records within a school year. The aggregated average daily attendance divided by the aggregated average daily membership provides the percentages reported. MN District Preschool totals at this time only include School Readiness district programs, and do not yet include Voluntary Prekindergarten or School Readiness Plus programs, which will be included in the future.",
                openByDefault: false,

                views: [{
                        type: "template",
                        template: "<div class='covMsg'>{{data.covidMessage}}</div><h5>{{data.heading}}</h5> ",
                        dataSourceName: "data",
                        dataPath: "dataSets.attendanceAndECEParticipation"
                    }, {
                        type: "template",
                        template: "<p>NOTE: The total number of kindergartners in the Kindergarten Attendance report differs from other Kindergarten reports due to student-counting policies. Student enrollment is based on the count of students as of October 1 of the school year. Attendance is an end-of-year count of public school students with <a href='https://education.mn.gov/mdeprod/idcplg?IdcService=GET_FILE&dDocName=MDE074759&RevisionSelectionMethod=latestReleased&Rendition=primary' target='_blank'>average daily membership</a> (ADM) of at least one day. Generally, ADM is lower than enrollment due to such factors as student attrition, shared-time/homeschool students, and illness.</p><p>NOTE: Because ECFE serves children from birth to Kindergarten entrance, and ECFE data begins with the 2013-14 school year, ECLDS most fully represents ECFE participation in Kindergarten reports beginning with the 2019-20 school year.</p><button style='float:right;' onclick='exportChart(this.parentNode.nextSibling.children[0].id);'>Export to PDF</button>"
                    },

                    {
                        // SETUP
                        type: "stackedColumn",
                        dataSourceName: "data",
                        dataSetPath: "dataSets.attendanceAndECEParticipation",

                        // "pointsAcrossRows": given that the data is
                        // two-dimensional (meaning an array of arrays, or
                        // similar structure, implying that we have more than
                        // one series to plot) each series is contained in a
                        // "column", like so:
                        //	 +---------+
                        //	 | X . . . |
                        //	 | X . . . |
                        //	 | X . . . |
                        //	 | X . . . |
                        //	 +---------+
                        // To wrap your head around this, watch iteration in your debugger in
                        // pointsAcrossRows() in transforms.js.
                        dataShape: "pointsAcrossRows",
                        convertDataHashToArray: true,
                        dataDimensions: 2,
                        disableLegendClick: false,

                        // DISPLAY
                        xAxisLabelPath: "program",

                        legendReversed: false,
                        unitString: "%",
                        title: "",
                        unitDec: 1,

                        exportToPDF: true,
                        hideContext: true,
                        showLegend: true,
                        stackLabels: false,

                        // SERIES (only one series, this is a simple graph)
                        series: [{
                                valuePath: "attendance[0].percent",
                                seriesDisplayName: "Below 90%",

                            }, {
                                valuePath: "attendance[1].percent",
                                seriesDisplayName: "90%-95%",
                            },
                            {
                                valuePath: "attendance[2].percent",
                                seriesDisplayName: "96%-100%",

                            }
                        ],
                        errorConditions: [PluginPieces.standardErrorCondition]
                    },
                    {
                        type: "template",
                        dataSourceName: "data",
                        dataSetPath: "dataSets.attendanceAndECEParticipation",
                        template: attendanceAndECEParticipationTableTemplate,
                        errorConditions: [PluginPieces.silentStandardErrorCondition]
                    }
                ]
            }, {
                title: "For More Information",
                visibleByDefault: true,
                openByDefault: false,
                views: [{
                    type: "template",
                    template: " <h2> Glossary</h2><p><strong> Early Childhood Screening (ECS)</strong>:  ECS is a legislatively-required program that leads to earlier health and education interventions, if needed, and better outcomes for children. ECS is often a parent's first encounter with a school district and may be the first time they hear about or are referred to early learning programs; early childhood scholarships; Early Childhood Family Education; community programs; and medical, mental health, or special education services. The free screening is required for children attending public school kindergarten or first grade in Minnesota.  <a href='https://education.mn.gov/MDE/dse/early/elprog/scr/' target='_blank'>The Early Childhood Screening Program</a>  includes children screened by districts, some charter schools, as well as copies of comparable screenings submitted by parents from Head Start, Child and Teen Checkups, or public health and clinics. The included factors do not contain all known factors which may impact a child receiving screening such as lack of affordable transportation, homelessness/high mobility, or child/family health concerns. </p><p><strong> Kindergarten Attendance</strong>: The factors included reflect data currently available to ECLDS. The factors do not reflect all known factors which may impact attendance. However, many of these factors could be identified and referred for appropriate services as a part of early childhood screening: child or family chronic illness, lack of health care provider/insurance, lack of dental care, homelessness/high mobility, unreliable transportation or lack of affordable public transit system, lack of clean or affordable housing, bullying, misconceptions of families about the importance of school in the early years, schedule conflicts with families who work non-traditional hours, community factors, etc. (<a href='https://www.attendanceworks.org/' target='_blank'>Attendance Works</a>)</p><p><strong>Maternal Education</strong>: The mother's educational attainment at the time of the child's birth and data is identified in the child's birth record. Maternal Education is unknown for children born outside of Minnesota or children who were missing this data on their birth record (e.g. no birth data available).</p><p><strong>Minnesota District Preschool</strong>: As of June 2021, Voluntary Prekindergarten (VPK) and School Readiness Plus (SRP) data are included in the MN District Preschool data provided by MDE. School Readiness data begin with the 2013-2014 school year; VPK data begin with the 2016-2017 school year; SRP data begin with the 2017-2018 school year.</p><p><strong> Race/Ethnicity:</strong><ul style='margin-top: -7px; margin-bottom: -15px;'><li>Federal Race/Ethnicity categories are as reported by the child's education record and align with the federal <a href='https://www.govinfo.gov/content/pkg/FR-1997-10-30/pdf/97-28653.pdf' target='_blank'>Office of Management and Budget’s standards</a>.</li><li>State Race/Ethnicity categories are as follows:<ul style='padding-left: 1em;'><li  style='list-style: circle;'><strong>State (Minnesota) Definition of American Indian</strong>: A person having origins in any of the original peoples of North America and who maintains cultural identification through tribal affiliation or community recognition. Any student or descendant from a North American tribe, regardless of other racial backgrounds.</li><li  style='list-style: circle'><strong>State (Minnesota) Definition of Other Indigenous Peoples</strong>: A student whose only racial or ethnic identification is with the original peoples of Central or South America. A student who meets the federal definition of American Indian or Alaska Native but not the state definition of American Indian, which means they identify only with indigenous peoples from Central and South America.</li><li style='list-style: circle'>State (Minnesota) definitions of race/ethnicity other than American Indian and Other Indigenous Peoples align with federal categories</li></ul></li></ul></p><p><strong> Screening Unknown</strong>: Includes children who are receiving Early Childhood Special Education services and may have received a screening as a part of their evaluation; children who are screened by other providers but not entered into the system; or children who have no known screening record. For more information about kindergartner's cohorts and screening see MDE's <a href='https://public.education.mn.gov/MDEAnalytics/Data.jsp' target='_blank'>Data Center</a>, under Early Learning. </p><p><strong>Unknown Early Care and Education Participation</strong>: May include students who participated in private programs or were cared for at home or in a family, friend, or neighbor setting.</p><p>For more information, open the <a href='https://education.mn.gov/mdeprod/idcplg?IdcService=GET_FILE&dDocName=mde034818&RevisionSelectionMethod=latestReleased&Rendition=primary' target='_blank'>ECLDS Data Guide</a>.</p>"
                }]
            }],

            filterSources: {
                orgs: {
                    modelType: ECLDSModels.ECLDSOrgList,
                    //url: "/data/state.json",
                    url: "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_filter_orgid_kgAttend.fex",
                    noViewParams: true,
                    enabled: true
                },
                filters: {
                    modelType: CardboardModels.FilterProducer,
                    filterSources: ["filterForReport"],
                    urls: {
                        filterForReport: "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_filter_kgAttend_EcoDevRegion.fex"
                    },
                    enabled: true,
                    noViewParams: false
                },
                filters2: {
                    url: "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_filter_fiscal_year_kgAttend.fex",
                    enabled: true,
                    modelType: CardboardModels.FilterData
                },

                filters3: {
                    modelType: CardboardModels.FilterProducer,
                    filterSources: ["filterForReport"],
                    //    defaultParams: ["FISCAL_YEAR=2014"],
                    urls: {
                        filterForReport: "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_filter_kgAttend.fex"
                    },

                    enabled: true,
                    noViewParams: false
                },
                filters4: {
                    modelType: CardboardModels.FilterProducer,
                    filterSources: ["filterForReport"],
                    //    defaultParams: ["FISCAL_YEAR=2014"],
                    urls: {
                        filterForReport: "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_filter_kgAttend_nbr2.fex"
                    },

                    enabled: true,
                    noViewParams: false
                },

                demographicCategories: {
                    url: "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_filter_kgAttend_nbr3",
                    enabled: true,
                    modelType: CardboardCollections.DemoCatProducer,
                    noViewParams: false
                }
            },

            dataSources: {
                data: {
                    modelType: CardboardModels.Model,
                    //url:"/data/tempChildDemo.json",
                    url: "/ibi_apps/WFServlet?IBIAPP_app=ECLDSwebsvc&IBIF_ex=ecldsws_getdata_sec_z1_kgAttend",
                    yearPath: "meta.fiscalYear", // where to look in the JSON for the year
                    //	defaultParams: {
                    //		orgId: 999999000,
                    //      COHORTID: 2014
                    //	},
                    errorConditions: [PluginPieces.standardErrorCondition],
                
                    enabled: true
                }
            }
        };

        return exports;
    });
